import UrlService from '../UrlService';

import {
  calendarValidators,
  crmAccountValidators,
  crmContactValidators,
  gmailValidators,
  googleCalendarValidators,
  linkedinContactValidators,
  linkedinSearchValidators,
} from './validators';

export type CustomScrapingEventDetail<T = unknown> = { data: T; url?: URL };
export type CustomScrapingEvent<T = unknown> = CustomEvent & { detail: CustomScrapingEventDetail<T> };
type Validator = {
  name: string;
  validator: (data: unknown) => boolean;
};

const createScrappingMonitoringService = () => {
  const getPage = (url: URL) => {
    const urlMappings = [
      { check: UrlService.isLinkedinContactPage, result: 'Linkedin contact' },
      { check: UrlService.isLinkedinSearchPage, result: 'Linkedin search' },
      { check: UrlService.isLinkedinSearchAllPage, result: 'Linkedin search all' },
      { check: UrlService.isGoogleCalendarUrl, result: 'Google calendar' },
      { check: UrlService.isOutlookCalendarUrl, result: 'Outlook calendar' },
      { check: UrlService.isGmailUrl, result: 'Gmail' },
      { check: UrlService.isSalesforceAccountPage, result: 'Salesforce account' },
      { check: UrlService.isSalesforceContactPage, result: 'Salesforce contact' },
      { check: UrlService.isSalesforceLeadPage, result: 'Salesforce lead' },
      { check: UrlService.isSalesforceLightningAccountPage, result: 'Salesforce lightning account' },
      { check: UrlService.isSalesforceLightningContactPage, result: 'Salesforce lightning contact' },
      { check: UrlService.isSalesforceLightningLeadPage, result: 'Salesforce lightning lead' },
      { check: UrlService.isSalesloftCompanyPage, result: 'Salesloft company' },
      { check: UrlService.isSalesloftContactPage, result: 'Salesloft contact' },
      { check: UrlService.isHubspotCompanyPage, result: 'Hubspot account' },
      { check: UrlService.isHubspotContactPage, result: 'Hubspot contact' },
      { check: UrlService.isHubspotDealPage, result: 'Hubspot lead' },
    ];

    for (const mapping of urlMappings) {
      if (mapping.check(url)) {
        return mapping.result;
      }
    }

    return 'unknown';
  };

  const getScrappingConfig = (url: URL): { page: string; validators: Validator[] } => {
    const page = getPage(url);
    if (UrlService.isLinkedinContactPage(url)) {
      return { page, validators: linkedinContactValidators };
    }

    if (UrlService.isLinkedinSearchPage(url) || UrlService.isLinkedinSearchAllPage(url)) {
      return { page, validators: linkedinSearchValidators };
    }

    if (UrlService.isGoogleCalendarUrl(url)) {
      return { page, validators: googleCalendarValidators };
    }
    if (UrlService.isOutlookCalendarUrl(url)) {
      return { page, validators: calendarValidators };
    }

    if (UrlService.isGmailUrl(url)) {
      return { page, validators: gmailValidators };
    }

    if (
      UrlService.isSalesforceAccountPage(url) ||
      UrlService.isSalesforceLightningAccountPage(url) ||
      UrlService.isHubspotCompanyPage(url) ||
      UrlService.isSalesforceLeadPage(url) ||
      UrlService.isSalesforceLightningLeadPage(url) ||
      UrlService.isHubspotDealPage(url)
    ) {
      return { page, validators: crmAccountValidators };
    }

    if (
      UrlService.isSalesforceContactPage(url) ||
      UrlService.isSalesforceLightningContactPage(url) ||
      UrlService.isHubspotContactPage(url)
    ) {
      return { page, validators: crmContactValidators };
    }

    return { page, validators: [] };
  };

  const dispatchScrappingEvent = (detail: CustomScrapingEventDetail) => {
    document.dispatchEvent(new CustomEvent('scrapingEvent', { detail }));
  };

  return {
    getScrappingConfig,
    dispatchScrappingEvent,
  };
};

const ScrappingMonitoringService = createScrappingMonitoringService();

export default ScrappingMonitoringService;
